import React, { useEffect, useState, useMemo } from 'react';
import { Col, Button } from 'react-bootstrap';
import Modal from 'react-bootstrap/Modal';
import { RiAlertFill, RiInformationLine } from 'react-icons/ri';
import { FiShoppingBag } from 'react-icons/fi';
//translations
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import useLocaleNavigate from 'utils/customHooks/useLocaleNavigate';
// Zustand
import { useCartStore } from 'stores/cartStore';
import { useUserStore } from 'stores/userStore';
import QuickOrderModal from 'pages/home/QuickOrderModal';
import useAssignedLibrary from 'utils/customHooks/useAssignedLibrary';
// Cart Utils
import { formatCurrency } from 'utils/currencyFormatter';
import {
  calculateTotalOrderSummary,
  calculateDiscountAmount,
  calculateTotalOrderWithDiscount,
} from 'components/cart/utils';
//API
import { validateGiftCard } from 'api/orders';
import { removeItemCart } from 'api/ecommerce';

const OrderSummary = ({ activeStep = null }) => {
  const { t } = useTranslation(['cart', 'home']);
  const {
    countItems,
    subtotal,
    iva,
    total,
    shippingCosts,
    items,
    isShippingAvailable,
    errorAmountPrice,
    giftCard,
    setTotalWithDiscount,
    setErrorAmountPrice,
    clear,
    discountCart,
    totalWithDiscount,
    setIsGiftOrder
  } = useCartStore();
  const { isConcourseUser, info_concourse } = useUserStore();
  const { assignedLibrary } = useAssignedLibrary();

  const navigate = useLocaleNavigate();
  const location = useLocation();
  const noExist = location.state?.noExist || [];
  const noStock = location.state?.noStock || [];
  const hasGiftCard = giftCard && (giftCard.discount_amount > 0);
  const [showModal, setShowModal] = useState(false);
  const [giftCardResponse, setGiftCardResponse] = useState(null);
  const [showQuickOrder, setShowQuickOrder] = useState(false);

  const isGiftCard = discountCart?.type === 'gift_card';
  const showFollowingButton = activeStep === 0 && countItems > 2;
  const shouldDisableFollowingButton =
    activeStep !== 0 || (isGiftCard && totalWithDiscount <= 0);

  const handleOpenQuickOrder = () => setShowQuickOrder(true);
  const handleCloseQuickOrder = () => setShowQuickOrder(false);

  // Los totales ya vienen calculados correctamente del store
  const ivaConGiftCard = iva;
  const subtotalToShow = subtotal;

  const amountDiscount = useMemo(() => {
    if (discountCart?.type === 'percentage') {
      return calculateDiscountAmount(subtotal, iva, discountCart?.quantity);
    } else if (discountCart?.type === 'gift_card') {
      return discountCart?.quantity;
    }
    return 0;
  }, [discountCart, subtotal, iva]);

  const concursoDiscountAmount = useMemo(() => {
    if (!isConcourseUser || !items?.length) return 0;
    return items.reduce((sum, item) => {
      const original = Number(item.originalPrice);
      const price = Number(item.price);
      if (original > 0 && original > price) {
        return sum + (original - price) * (item.quantity || 1);
      }
      return sum;
    }, 0);
  }, [isConcourseUser, items]);

  const totalDiscountToShow = (discountCart?.has_discount ? (discountCart?.type === 'gift_card' ? discountCart.quantity : amountDiscount) : 0) + concursoDiscountAmount;

  const totalOrderSummaryDiscount = useMemo(() => {
    const totalConDescuento = calculateTotalOrderWithDiscount(subtotal, iva, amountDiscount, shippingCosts);
    return Math.max(0, totalConDescuento).toFixed(2);
  }, [amountDiscount, subtotal, iva, shippingCosts]);

  useEffect(() => {
    setErrorAmountPrice(Number(totalOrderSummaryDiscount) < 0);
  }, [totalOrderSummaryDiscount, setErrorAmountPrice]);

  useEffect(() => {
    const totalFinal = Number(totalOrderSummaryDiscount);
    setTotalWithDiscount(Math.max(0, totalFinal));
  }, [hasGiftCard, discountCart, totalOrderSummaryDiscount, total, setTotalWithDiscount]);

  const paymentByGiftCard = async () => {
    try {
      const appliedCoupon = discountCart?.code;
      const payload = { applied_coupon: appliedCoupon };
      const response = await validateGiftCard(payload);
      if (response.status?.status === 400) {
        throw new Error(response);
      }
      if (response.data?.error) {
        throw new Error(response);
      }
      setGiftCardResponse(response.data);
      setShowModal(true);
    } catch (error) {
      const errorMessage = error?.response?.status?.data?.error || t('anUnexpectedErrorOccurred');
      setGiftCardResponse({ error: errorMessage });
      setShowModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    clear();
    navigate('/');
    setGiftCardResponse(null);
  };

  const handleGoToShipping = () => {
    if (!shouldDisableFollowingButton) {
      setIsGiftOrder(false);
      navigate(`/${assignedLibrary}/envio`);
    }
  };

  const handleClearOrder = async () => {
    try {
      if (Array.isArray(items)) {
        await Promise.all(
          items.map((item) => removeItemCart({ item: item.id }))
        );
      }
    } catch (e) {
      console.error(e);
    } finally {
      clear();
    }
  };

  const articlesLabel =
    countItems === 1
      ? t('articleSingular', { count: countItems })
      : t('articlePlural', { count: countItems });

  const displayTotal = formatCurrency(
    discountCart?.has_discount
      ? totalOrderSummaryDiscount
      : calculateTotalOrderSummary(discountCart, total, shippingCosts)
  );

  return (
    <Col md={12} lg={12} className="checkout-summary-col mb-2">
      <div className="checkout-summary">
        <div className="checkout-summary__card">
          <div className="checkout-summary__header">
            <div className="checkout-summary__title-wrap">
              <FiShoppingBag className="checkout-summary__bag-icon" aria-hidden="true" />
              <h2 className="checkout-summary__title">{t('yourOrder')}</h2>
            </div>
            <span className="checkout-summary__count">{articlesLabel}</span>
          </div>
          <div className="checkout-summary__body">
            <div className="checkout-summary__line">
              <span>{t('subtotal')}</span>
              <span>{subtotalToShow ? formatCurrency(subtotalToShow) : ''}</span>
            </div>
            <div className="checkout-summary__line">
              <span>{t('VAT')}</span>
              <span>{ivaConGiftCard ? formatCurrency(ivaConGiftCard) : ''}</span>
            </div>
            {totalDiscountToShow > 0 && (
              <div className="checkout-summary__line textDiscount">
                <span>
                  {concursoDiscountAmount > 0
                    ? t('discountIncludedPercent', { percent: Number(info_concourse?.discount) || 15 })
                    : discountCart?.type === 'gift_card'
                      ? t('discountGiftCard')
                      : t('discount')}
                </span>
                <span>
                  {concursoDiscountAmount > 0
                    ? `-${formatCurrency(concursoDiscountAmount)}`
                    : `-${formatCurrency(
                        discountCart?.type === 'gift_card' ? discountCart.quantity : amountDiscount
                      )}`}
                </span>
              </div>
            )}
            {(!discountCart?.has_discount || discountCart?.type === 'gift_card') && (
              <div className="checkout-summary__line">
                <span>{t('shippingCosts')}</span>
                <span>
                  {shippingCosts ? formatCurrency(shippingCosts) : <b>{t('freeShipping')}</b>}
                </span>
              </div>
            )}
            {discountCart?.type === 'percentage' && (
              <>
                <div className="checkout-summary__line">
                  <span>{t('totalWithoutDiscount')}</span>
                  <span>{formatCurrency(calculateTotalOrderSummary(discountCart, total, shippingCosts))}</span>
                </div>
                <div className="checkout-summary__line textDiscount">
                  <span>{t('discount')}</span>
                  <span>{`${discountCart.quantity} %`}</span>
                </div>
                <div className="checkout-summary__line textDiscount">
                  <span>{t('discountAmount')}</span>
                  <span>{formatCurrency(amountDiscount)}</span>
                </div>
              </>
            )}
            <div className="checkout-summary__divider" role="separator" />
            <div className="checkout-summary__total">
              <span>{discountCart?.has_discount ? t('totalWithDiscount') : t('total')}</span>
              <span className={errorAmountPrice ? 'text-danger' : ''}>{displayTotal}</span>
            </div>
            {discountCart?.type === 'gift_card' && Number(totalOrderSummaryDiscount) <= 0 && (
              <div className="text-center mt-3">
                <Button className="checkout-btn checkout-btn--blue" onClick={paymentByGiftCard}>
                  {t('useGiftCard')}
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="checkout-summary__extras">
          {activeStep === 0 && shippingCosts > 0 && (
            <p>
              <RiInformationLine className="icon-md" /> {t('shippingNote')}
            </p>
          )}
          {activeStep === 1 && !isShippingAvailable?.availability && (
            <p>
              <RiAlertFill className="icon-md" /> {t(`${isShippingAvailable?.message}`)}
            </p>
          )}
          {errorAmountPrice && (
            <p className="text-danger mt-2">
              <RiAlertFill className="icon-md" /> {t('errorPriceAmount')}
            </p>
          )}
          {countItems > 0 && (
            <div className="mt-2 d-flex justify-content-between gap-2 flex-wrap">
              <Button
                variant="outline-danger"
                className={`checkout-btn checkout-btn--ghost flex-grow-1${showFollowingButton ? '' : ''}`}
                onClick={handleClearOrder}
              >
                {t('clearCart')}
              </Button>
              {showFollowingButton && (
                <Button
                  className="checkout-btn checkout-btn--primary flex-grow-1"
                  disabled={shouldDisableFollowingButton}
                  onClick={handleGoToShipping}
                >
                  {t('following')}
                </Button>
              )}
            </div>
          )}
          <div className="checkout-summary__quick-order quick-order-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0 quick-order-title">{t('quickOrder', { ns: 'home' })}</h5>
            <Button className="quick-order-button" variant="primary" size="sm" onClick={handleOpenQuickOrder}>
              {t('add', { ns: 'home' })}
            </Button>
          </div>
          {Array.isArray(noExist) && noExist.length > 0 && (
            <p className="mt-2 cart-nonexisting-eans text-danger">
              <RiAlertFill className="icon-md text-danger" />{' '}
              {noExist.length === 1
                ? t('eanDoesNotExist', { ns: 'home', codes: noExist.join(', ') })
                : t('eansDoNotExist', { ns: 'home', codes: noExist.join(', ') })}
            </p>
          )}
          {Array.isArray(noStock) && noStock.length > 0 && (
            <p className="mt-1 cart-nonexisting-eans text-danger">
              <RiAlertFill className="icon-md text-danger" />{' '}
              {noStock.length === 1
                ? t('eanNoStock', { ns: 'home', codes: noStock.join(', ') })
                : t('eansNoStock', { ns: 'home', codes: noStock.join(', ') })}
            </p>
          )}
        </div>
      </div>

      {giftCardResponse && (
        <Modal
          show={showModal}
          onHide={handleCloseModal}
          centered
          dialogClassName={giftCardResponse.error ? 'modal-gift-error' : 'modal-gift'}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {giftCardResponse.error ? `❌ ${t('cardError')}` : `🎁 ${t('giftCardApplied')}`}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              {giftCardResponse.error
                ? giftCardResponse.error
                : giftCardResponse.explanation || t('cardAppliedCorrectly')}
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={handleCloseModal}>
              {t('accept')}
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      <QuickOrderModal show={showQuickOrder} onClose={handleCloseQuickOrder} />
    </Col>
  );
};

export default OrderSummary;
